import { SelectFilter } from "@design-system/shadcn";
import React, { useCallback, useMemo } from "react";

import { useFieldContext } from "../core/form-contexts";
import { useFieldDescribedBy } from "../core/use-field-described-by";

type FormFieldSelectFilterProps = Omit<
  React.ComponentProps<typeof SelectFilter>,
  "value" | "onValueChange"
> & {
  value?: string;
  onValueChange?: (value: string | undefined) => void;
};

function FormFieldSelectFilter({
  value,
  onValueChange,
  ...props
}: FormFieldSelectFilterProps) {
  const field = useFieldContext<string>();

  if (!field) {
    throw new Error("FormFieldSelectFilter must be used within form.AppField");
  }

  const hasError = useMemo(
    () => field.state.meta.errors.length > 0,
    [field.state.meta.errors.length]
  );

  const describedBy = useFieldDescribedBy(props["aria-describedby"]);

  const changeHandler = useCallback(
    (val: string | undefined) => field.handleChange(val ?? ""),
    [field]
  );

  const labelId = `${field.name}-label`;

  return (
    <SelectFilter
      id={field.name}
      value={value ?? (field.state.value || undefined)}
      onValueChange={onValueChange ?? changeHandler}
      aria-invalid={hasError}
      aria-labelledby={labelId}
      {...props}
      aria-describedby={describedBy}
    />
  );
}

export { FormFieldSelectFilter };
export type { FormFieldSelectFilterProps };
